import type { ApiError, PagedResult } from '../types'

const BASE_URL = `${import.meta.env.VITE_API_URL ?? ''}/api`

let accessToken: string | null = null
let isRefreshing = false
let refreshQueue: Array<{
  resolve: (token: string) => void
  reject: (error: Error) => void
}> = []

export function setToken(token: string | null) {
  accessToken = token
}

export function setRefreshToken(token: string | null) {
  if (!token) return
}

export function getToken() {
  return accessToken
}

// Auth store'dan token'ları oku (circular import'u önlemek için lazy)
function getStoredTokens() {
  try {
    const raw = localStorage.getItem('crm-auth')
    if (!raw) return { access: null, refresh: null }
    const state = JSON.parse(raw)?.state
    return { access: state?.accessToken ?? null, refresh: state?.refreshToken ?? null }
  } catch {
    return { access: null, refresh: null }
  }
}

function updateStoredAuth(data: {
  accessToken?: string
  refreshToken?: string
  user?: unknown
  tenant?: unknown
}) {
  try {
    const raw = localStorage.getItem('crm-auth')
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (parsed?.state) {
      if (data.accessToken) parsed.state.accessToken = data.accessToken
      if (data.refreshToken) parsed.state.refreshToken = data.refreshToken
      if (data.user) parsed.state.user = data.user
      if (data.tenant) parsed.state.tenant = data.tenant
      localStorage.setItem('crm-auth', JSON.stringify(parsed))
    }
  } catch { /* ignore */ }
}

async function tryRefresh(): Promise<string | null> {
  const { refresh } = getStoredTokens()
  if (!refresh) return null

  const res = await fetch(`${BASE_URL}/auth/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: refresh }),
  })

  if (!res.ok) return null

  const raw = await res.json()
  const data = unwrapEnvelope<AuthRefreshResponse>(raw).data
  const newToken: string = data.accessToken
  if (!newToken) return null

  accessToken = newToken
  updateStoredAuth(data)
  return newToken
}

async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      refreshQueue.push({ resolve, reject })
      setTimeout(() => reject(new Error('Token refresh timeout')), 10000)
    })
  }

  isRefreshing = true
  const newToken = await tryRefresh()
  isRefreshing = false

  if (newToken) {
    refreshQueue.forEach(item => item.resolve(newToken))
  } else {
    refreshQueue.forEach(item => item.reject(new Error('Token refresh failed')))
  }
  refreshQueue = []

  return newToken
}

async function requestRaw(path: string, init: RequestInit = {}) {
  // Token yoksa localStorage'dan al
  if (!accessToken) {
    const { access } = getStoredTokens()
    accessToken = access
  }

  const requestedMethod = (init.method ?? 'GET').toUpperCase()
  const needsMethodOverride = requestedMethod === 'PUT' || requestedMethod === 'PATCH' || requestedMethod === 'DELETE'
  const transportInit: RequestInit = needsMethodOverride
    ? {
        ...init,
        method: 'POST',
        headers: {
          ...(init.headers as Record<string, string>),
          'X-Dotkesk-Method': requestedMethod,
        },
      }
    : init

  const makeHeaders = (token: string | null): Record<string, string> => ({
    'Content-Type': 'application/json',
    ...(transportInit.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  })

  let res = await fetch(`${BASE_URL}${path}`, { ...transportInit, headers: makeHeaders(accessToken) })

  // 401/403 → token yenile ve bir kez tekrar dene.
  // 403 için bu özellikle yeni rol/izin verilen eski token'ları toparlar.
  if ((res.status === 401 || res.status === 403) && !path.startsWith('/auth/')) {
    const shouldLogoutOnFailure = res.status === 401
    const newToken = await refreshAccessToken().catch(() => null)

    if (newToken) {
      res = await fetch(`${BASE_URL}${path}`, { ...transportInit, headers: makeHeaders(newToken) })
    } else if (shouldLogoutOnFailure) {
      localStorage.removeItem('crm-auth')
      window.location.href = '/login'
      throw { message: 'Oturum süresi doldu.' } as ApiError
    }
  }

  return res
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await requestRaw(path, init)

  if (res.status === 204) return undefined as T

  const data = await res.json().catch(() => null)

  const envelope = unwrapEnvelope<T>(data)

  if (!res.ok || envelope.success === false) {
    const err: ApiError = toApiError(data, envelope.message, res.status)
    redirectSuspendedAccount(err)
    throw err
  }

  return envelope.data as T
}

async function requestEnvelope<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const res = await requestRaw(path, init)

  if (res.status === 204) {
    return { data: undefined as T, success: true, message: undefined }
  }

  const data = await res.json().catch(() => null)
  const envelope = unwrapEnvelope<T>(data)

  if (!res.ok || envelope.success === false) {
    const err: ApiError = toApiError(data, envelope.message, res.status)
    redirectSuspendedAccount(err)
    throw err
  }

  return envelope
}

function toApiError(data: any, fallbackMessage: string | undefined, status: number): ApiError {
  const error = data?.Error ?? data?.error
  return {
    message: error?.Message ?? error?.message ?? fallbackMessage ?? data?.message ?? `HTTP ${status}`,
    code: error?.Code ?? error?.code ?? data?.code,
    args: error?.Args ?? error?.args,
    traceId: error?.TraceId ?? error?.traceId,
    errors: data?.Errors ?? data?.errors,
  }
}

function redirectSuspendedAccount(error: ApiError) {
  if (error.code !== 'account.tenant_suspended' && error.code !== 'account.user_suspended') return

  accessToken = null
  localStorage.removeItem('crm-auth')
  const suspended = error.code === 'account.tenant_suspended' ? 'tenant' : 'user'
  if (window.location.pathname !== '/login') {
    window.location.href = `/login?suspended=${suspended}`
  }
}

function unwrapEnvelope<T>(data: any): ApiEnvelope<T> {
  if (!data || typeof data !== 'object') return { data }

  const hasPascalEnvelope = 'Data' in data && 'Success' in data
  const hasCamelEnvelope = 'data' in data && 'success' in data
  if (!hasPascalEnvelope && !hasCamelEnvelope) return { data }

  return {
    data: hasPascalEnvelope ? data.Data : data.data,
    success: hasPascalEnvelope ? data.Success : data.success,
    message: hasPascalEnvelope ? data.Message : data.message,
    totalCount: hasPascalEnvelope ? data.TotalCount : data.totalCount,
    page: hasPascalEnvelope ? data.Page : data.page,
    pageSize: hasPascalEnvelope ? data.PageSize : data.pageSize,
  }
}

function toPagedResult<T>(envelope: ApiEnvelope<T[]>, fallbackPage = 1, fallbackPageSize = 25): PagedResult<T> {
  const items = Array.isArray(envelope.data) ? envelope.data : []
  const page = envelope.page ?? fallbackPage
  const pageSize = envelope.pageSize ?? fallbackPageSize
  const totalCount = envelope.totalCount ?? items.length
  return {
    items,
    totalCount,
    page,
    pageSize,
    pageNumber: page,
    totalPages: Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize))),
  }
}

export const api = {
  stream: async (path: string, body: unknown, signal?: AbortSignal): Promise<Response> => {
    const res = await requestRaw(path, { method: 'POST', body: JSON.stringify(body), signal, headers: { Accept: 'text/event-stream' } })
    if (!res.ok) {
      const data = await res.json().catch(() => null)
      const envelope = unwrapEnvelope(data)
      const error = toApiError(data, envelope.message, res.status)
      redirectSuspendedAccount(error)
      throw error
    }
    if (!res.headers.get('Content-Type')?.startsWith('text/event-stream') || !res.body)
      throw { code: 'ai.invalid_stream', message: 'Invalid streaming response.' } as ApiError
    return res
  },
  get:    <T>(path: string)                  => request<T>(path),
  post:   <T>(path: string, body?: unknown)  => request<T>(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:    <T>(path: string, body?: unknown)  => request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch:  <T>(path: string, body?: unknown)  => request<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown)  => request<T>(path, { method: 'DELETE', body: body !== undefined ? JSON.stringify(body) : undefined }),
  getEnvelope: <T>(path: string)             => requestEnvelope<T>(path),
  getPaged: async <T>(path: string, page = 1, pageSize = 25) =>
    toPagedResult(await requestEnvelope<T[]>(path), page, pageSize),
}

interface ApiEnvelope<T> {
  data: T | any
  success?: boolean
  message?: string
  totalCount?: number
  page?: number
  pageSize?: number
}

interface AuthRefreshResponse {
  accessToken: string
  refreshToken?: string
  user?: unknown
  tenant?: unknown
}
